import { useEffect, useMemo, useState } from "react";
import { OverlayData } from "contexts/OverlayContext";
import { useEntities } from "@hooks/useEntities";
import { EliminatingEntryDrawer } from "@components/accounting/eliminatingEntryDrawer/EliminatingEntryDrawer";
import { useJournalEntryConsolidationContext } from "./journalEntryConsolidationContext";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import { JournalEntryDrawer } from "./JournalEntryDrawer";
import {
    isJournalEntryScopeEqual,
    JournalEntryScope,
} from "./journalEntryScope";
import { resolveJournalEntryScope } from "./journalEntryScopeRouting";

interface JournalEntryDrawerControllerProps {
    data: NonNullable<OverlayData<"journalEntryDrawer">>;
    open: boolean;
    onClose: () => void;
}

export function JournalEntryDrawerController({
    data,
    open,
    onClose,
}: JournalEntryDrawerControllerProps) {
    const allEntities = useEntities();
    const availableEntities = useEntitiesWithAccountingAvailable();
    const consolidationResult = useJournalEntryConsolidationContext({
        entityId: data.entity.id,
    });
    const consolidationContext =
        consolidationResult.status === "ready"
            ? consolidationResult.context
            : undefined;
    const [selectedScope, setSelectedScope] = useState<JournalEntryScope>({
        kind: "entity",
        entityId: data.entity.id,
    });
    const resolvedSelection = useMemo(
        () =>
            resolveJournalEntryScope({
                selectedScope,
                availableEntities,
                initialEntity: data.entity,
                readyConsolidationGroupId: consolidationContext?.group.id,
            }),
        [
            availableEntities,
            consolidationContext?.group.id,
            data.entity,
            selectedScope,
        ],
    );
    const effectiveScope = resolvedSelection.scope;

    useEffect(() => {
        if (!isJournalEntryScopeEqual(selectedScope, effectiveScope)) {
            setSelectedScope(effectiveScope);
        }
    }, [effectiveScope, selectedScope]);

    const memberEntities = useMemo(() => {
        if (!consolidationContext) {
            return [];
        }

        const memberUuids = new Set(
            consolidationContext.group.members.map(
                (member) => member.entityUuid,
            ),
        );
        const otherMembers = allEntities.filter(
            (entity) =>
                memberUuids.has(entity.uuid) &&
                entity.uuid !== consolidationContext.parentEntity.uuid,
        );
        return [consolidationContext.parentEntity, ...otherMembers];
    }, [allEntities, consolidationContext]);

    if (
        effectiveScope.kind === "consolidationGroup" &&
        consolidationContext?.group.id === effectiveScope.consolidationGroupId
    ) {
        return (
            <EliminatingEntryDrawer
                key="consolidation-group"
                open={open}
                onClose={onClose}
                workspaceId={consolidationContext.workspaceId}
                groupId={consolidationContext.group.id}
                entities={memberEntities}
                scope={effectiveScope}
                scopeEntities={availableEntities}
                consolidationContext={consolidationContext}
                onScopeChange={setSelectedScope}
            />
        );
    }

    return (
        <JournalEntryDrawer
            key="entity"
            show={open}
            onHide={onClose}
            entity={resolvedSelection.entity}
            journalEntryId={data.journalEntryId}
            ledgerBasis={data.ledgerBasis}
            ledgerId={data.ledgerId}
            initialDate={data.initialDate}
            duplicateFrom={data.duplicateFrom}
            scope={effectiveScope}
            entities={availableEntities}
            consolidationContext={consolidationContext}
            onScopeChange={setSelectedScope}
        />
    );
}
